// ─────────────────────────────────────────────────────────────────────────────
// CEO Command Center — data hooks (executive-authority-rbac, task 10).
//
// TanStack Query hooks over the additive `/api/authority` + `/api/admin/*`
// governance surface (api-authority/index.js). All requests use the shared
// same-origin axios client (empty base → `/api/...`, cookie auth), so no path
// resolves to `/api/api/` (EA-10). Every governance endpoint is SUPER_ADMIN-only
// on the server; the client gate is presentation only — the server stays
// authoritative and returns an identical 403 to any non-executive.
// ─────────────────────────────────────────────────────────────────────────────

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { authorityPaths } from '../authorityPaths';
import type {
  AuthorityIdentity, SystemHealth, OrgSetting, AuditEntry, ApiKeySummary,
  CreatedApiKey, BackupRecord, OversightResult, OversightDomain,
} from '../types';

const KEY = {
  identity: ['command-center', 'identity'] as const,
  health: ['command-center', 'system-health'] as const,
  org: ['command-center', 'org'] as const,
  audit: (action?: string) => ['command-center', 'audit', { action: action ?? null }] as const,
  apiKeys: ['command-center', 'api-keys'] as const,
  backups: ['command-center', 'backups'] as const,
};

/** The caller's derived tier/attribution (never a raw role) — GET /api/authority/me. */
export function useAuthorityIdentity(enabled = true) {
  return useQuery({
    queryKey: KEY.identity,
    queryFn: async () => (await api.get<AuthorityIdentity>(authorityPaths.identity())).data,
    enabled,
  });
}

export function useSystemHealth(enabled = true) {
  return useQuery({
    queryKey: KEY.health,
    queryFn: async () => (await api.get<SystemHealth>(authorityPaths.systemHealth())).data,
    enabled,
    refetchInterval: 15_000,
  });
}

export function useOrgSettings(enabled = true) {
  return useQuery({
    queryKey: KEY.org,
    queryFn: async () => (await api.get<{ settings: OrgSetting[] }>(authorityPaths.org())).data.settings,
    enabled,
  });
}

export function useUpsertOrgSetting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: unknown }) =>
      (await api.put<{ setting: OrgSetting }>(authorityPaths.orgSetting(key), { value })).data.setting,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY.org }),
  });
}

export function useAuditLog(action: string | undefined, enabled = true) {
  return useQuery({
    queryKey: KEY.audit(action),
    queryFn: async () =>
      (await api.get<{ entries: AuditEntry[] }>(authorityPaths.audit(action))).data.entries,
    enabled,
  });
}

export function useApiKeys(enabled = true) {
  return useQuery({
    queryKey: KEY.apiKeys,
    queryFn: async () => (await api.get<{ keys: ApiKeySummary[] }>(authorityPaths.apiKeys())).data.keys,
    enabled,
  });
}

export function useCreateApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ label, scopes }: { label: string; scopes: string[] }) =>
      (await api.post<CreatedApiKey>(authorityPaths.apiKeys(), { label, scopes })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY.apiKeys }),
  });
}

export function useRevokeApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.post(authorityPaths.revokeApiKey(id))).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY.apiKeys }),
  });
}

export function useBackups(enabled = true) {
  return useQuery({
    queryKey: KEY.backups,
    queryFn: async () => (await api.get<{ backups: BackupRecord[] }>(authorityPaths.backups())).data.backups,
    enabled,
  });
}

export function useRunBackup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (kind: string) =>
      (await api.post<{ backup: BackupRecord }>(authorityPaths.backups(), { kind, confirm: true })).data.backup,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY.backups }),
  });
}

/** CEO oversight read — REQUIRES a justification (server denies without it) and
 *  is itself audit-logged server-side (audit-of-audit). */
export function useOversight() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain, justification }: { domain: OversightDomain; justification: string }) =>
      (await api.post<OversightResult>(authorityPaths.oversight(domain), { justification })).data,
    // A successful oversight read writes an audit entry — refresh the audit tab.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['command-center', 'audit'] }),
  });
}
